import Component from '../../components/component/component';
import Range from '../../components/forms/range/range';
import Helpers from '../../components/helpers/helpers';
import Icon from '../../components/icon/icon';
import Ajax, { IAjaxPostParams } from '../ajax/ajax';

import './calculator-steps.scss';

interface ICalculatorStepsInstallment {
    [key: string]: unknown;
    expectedTotal: number;
    expectedDate: string;
    principal: number;
    interest: number;
    admissionFee: number;
    accessFee: number;
    adminFee: number;
}

interface ICalculatorStepsColumn {
    key: string;
    label: string;
}

interface ICalculatorStepsValues {
    [key: string]: unknown;
    monthlyPayment: number;
    installments?: ICalculatorStepsInstallment[];
    loanProductId: number;
    currencyData?: {
        primaryCurrency: string;
        secondaryCurrency: string;
        primaryToSecondaryRate: number;
    };
    // only present for logged-in admins, for debugging
    rawResponse?: unknown;
}

interface ICalculatorStepsValuesResponse {
    success: boolean;
    errors?: string[];
    data?: ICalculatorStepsValues;
}

interface ICalculatorStepsSubmitResponse {
    success: boolean;
    customerId?: string;
    errorCode?: string;
    errors?: string[];
    invalidFields?: string[];
}

const LOADING_CLASS: string = 'is-loading';
const CURRENT_CLASS: string = 'is-current';
const COMPLETE_CLASS: string = 'is-complete';
const INVALID_CLASS: string = 'is-invalid';
const VISIBLE_CLASS: string = 'is-visible';

// step keys must match the data-panel/data-step attributes in calculator-steps.twig
const STEPS: string[] = ['amount', 'personal', 'consents'];

export default class CalculatorSteps extends Component {
    static initSelector: string = '.calculator-steps__card';

    private section: JQuery;
    private amountInput: JQuery;
    private periodInput: JQuery;
    private isLoanForFree: boolean;

    private valuesXhr: JQueryXHR;
    private submitXhr: JQueryXHR;

    private requiredMessage: string;
    private emailMessage: string;
    private formatMessages: { [key: string]: string };

    private primaryCurrency: string = '';
    private startedTracked: boolean = false;
    private formFilledTracked: boolean = false;

    constructor(target: HTMLElement) {
        super(target);

        this.requiredMessage = this.element.attr('data-field-error') || 'This field is required.';
        this.emailMessage = this.element.attr('data-email-error') || this.requiredMessage;
        // wrong-format messages per field name, used instead of the browser's own patternMismatch text
        this.formatMessages = {
            phone: this.element.attr('data-phone-error') || this.requiredMessage,
            pin: this.element.attr('data-pin-error') || this.requiredMessage,
        };

        this.section = this.element.closest('.calculator-steps');
        this.amountInput = this.element.find('input[name="amount"]');
        this.periodInput = this.element.find('[name="period"]');
        this.isLoanForFree = this.element.is('[data-loan-for-free]');

        this.amountInput.on('change', this.handleValuesChange);
        this.periodInput.on('change', this.handleValuesChange);

        // the range component suppresses Enter on keypress, so recalculate from keydown instead
        this.amountInput.on('keydown', (event: JQuery.TriggeredEvent): void => {
            if (event.which === 13) {
                event.preventDefault();
                this.amountInput.trigger('change');
                // dismiss the virtual keyboard on mobile
                this.amountInput.trigger('blur');
            }
        });

        this.element.find('.calculator-steps__range').each((index: number, element: HTMLElement): void => {
            const rangeInstance: InstanceType<typeof Range> = Range.create<typeof Range>(element);

            rangeInstance.sliderContainer.on('change', this.handleValuesChange);
        });

        this.element.find('.js-cs-apply').on('click', (): void => {
            if (!this.startedTracked) {
                this.startedTracked = true;
                Helpers.pushDataLayerEvent('application_started');
            }

            this.goToStep('personal');
        });
        this.element.find('.js-cs-back').on('click', (event: JQuery.TriggeredEvent): void => this.goToStep($(event.currentTarget).data('back') as string));
        this.element.find('.js-cs-continue').on('click', this.handleContinue);
        this.element.on('submit', this.handleSubmit);

        this.element.find('input, select').on('input change', (event: JQuery.TriggeredEvent): void => {
            this.clearFieldError(event.currentTarget as HTMLInputElement);
        });

        this.fetchValues(false);
    }

    private handleValuesChange: () => void = (): void => {
        this.fetchValues(true);
    };

    private fetchValues(showLoading: boolean): void {
        const amount: number = parseInt(this.amountInput.val() as string, 10);
        const period: number = parseInt(this.periodInput.val() as string, 10);

        if (!amount || !period) {
            return;
        }

        if (this.valuesXhr) {
            this.valuesXhr.abort();
        }

        if (showLoading) {
            this.element.addClass(LOADING_CLASS);
        }

        const params: IAjaxPostParams = { action: 'calculator-values', amount, period };

        if (this.isLoanForFree) {
            params.loanForFree = 1;
        }

        this.valuesXhr = Ajax.post(params, { async: true });
        this.valuesXhr.done((response: ICalculatorStepsValuesResponse): void => {
            if (response.success && response.data) {
                this.updateValues(response.data);
            }
        });
        this.valuesXhr.always((): void => {
            if (showLoading) {
                this.element.removeClass(LOADING_CLASS);
            }
        });
    }

    private updateValues(values: ICalculatorStepsValues): void {
        const rate: number | undefined = values.currencyData?.primaryToSecondaryRate;

        if (values.currencyData?.primaryCurrency) {
            this.primaryCurrency = values.currencyData.primaryCurrency;
        }

        this.element.find('.js-cs-value').each((index: number, element: HTMLElement): void => {
            const key: string = element.getAttribute('data-key');
            const raw: unknown = key === 'monthlyPaymentSecondary'
                ? (rate ? values.monthlyPayment * rate : undefined)
                : values[key];

            if (typeof raw === 'number' && isFinite(raw)) {
                element.textContent = CalculatorSteps.formatNumber(raw, element.getAttribute('data-format') === 'percent');
            }
        });

        if (values.loanProductId) {
            this.element.find('input[name="product_id"]').val(values.loanProductId);
        }

        this.updateInstallments(values);
    }

    private updateInstallments(values: ICalculatorStepsValues): void {
        const link: JQuery = this.element.find('.calculator-steps__installments-link');
        const content: JQuery = $('#calculator-steps-installments .modal__content');
        const columns: ICalculatorStepsColumn[] = link.length ? (link.data('columns') as ICalculatorStepsColumn[]) || [] : [];

        if (!columns.length || !content.length || !values.installments || !values.installments.length) {
            return;
        }

        let html: string = '<table class="calculator-steps__installments"><thead><tr><th scope="col"></th>';

        columns.forEach((column: ICalculatorStepsColumn): void => {
            html += `<th scope="col">${column.label || ''}</th>`;
        });

        html += '</tr></thead><tbody>';

        // the API's first row is the disbursement (nr 0, negative principal), not an installment
        values.installments.slice(1).forEach((installment: ICalculatorStepsInstallment, index: number): void => {
            html += `<tr><td>${index + 1}</td>`;

            columns.forEach((column: ICalculatorStepsColumn): void => {
                html += `<td>${CalculatorSteps.formatCell(installment[column.key])}</td>`;
            });

            html += '</tr>';
        });

        html += '</tbody></table>';

        content.html(html);
    }

    private static formatCell(value: unknown): string {
        if (typeof value === 'number' && isFinite(value)) {
            return CalculatorSteps.formatNumber(value);
        }

        return typeof value === 'string' ? value : '';
    }

    private goToStep(step: string): void {
        this.element.attr('data-current-step', step);

        this.element.find('.calculator-steps__panel').removeClass(CURRENT_CLASS);
        this.element.find(`.calculator-steps__panel[data-panel="${step}"]`).addClass(CURRENT_CLASS);

        const currentIndex: number = STEPS.indexOf(step);

        this.element.find('.calculator-steps__progress-item').each((index: number, element: HTMLElement): void => {
            $(element)
                .toggleClass(COMPLETE_CLASS, index < currentIndex)
                .toggleClass(CURRENT_CLASS, index === currentIndex);
        });

        this.scrollToTop();
    }

    // GA4 measures funnel completion, so it fires whenever the success screen is shown, whatever the API returned
    private showResult(result: string, customerId: string = ''): void {
        if (result === 'success') {
            this.trackApplicationSuccessful(customerId);
        }

        this.element.attr('data-current-step', 'result');
        this.element.find('.calculator-steps__panel').removeClass(CURRENT_CLASS);
        this.element.find(`.calculator-steps__panel[data-panel="result-${result}"]`).addClass(CURRENT_CLASS);

        this.scrollToTop();
    }

    private scrollToTop(): void {
        const target: JQuery = this.section.length ? this.section : this.element;
        const top: number = Math.max(0, target.offset().top - (window.gotoAndPlay.headerOffset || 0));

        $('html, body').stop(true).animate({ scrollTop: top }, 300);
    }

    private validatePanel(panel: string): boolean {
        const fields: JQuery = this.element.find(`.calculator-steps__panel[data-panel="${panel}"]`).find('input, select');
        let isValid: boolean = true;

        fields.each((index: number, element: HTMLElement): void => {
            const input: HTMLInputElement = element as HTMLInputElement;
            // the select component's placeholder option has a non-empty value,
            // so a required select passes native validation without a real choice
            const isEmptySelect: boolean = element.tagName === 'SELECT' && input.required && input.value === 'placeholder';

            if (!input.checkValidity() || isEmptySelect) {
                isValid = false;
                this.setFieldError(input);
            } else {
                this.clearFieldError(input);
            }
        });

        this.element.find(`.calculator-steps__panel-error[data-error="${panel}"]`).toggleClass(VISIBLE_CLASS, !isValid);

        return isValid;
    }

    // renders a per-field message inside the field's error slot, mirroring the styleguide/GF markup
    private setFieldError(input: HTMLInputElement): void {
        const wrapper: JQuery = $(input).closest('.textfield, .check');

        wrapper.addClass(INVALID_CLASS);

        // checkboxes render an inline message with the error-outline icon, mirroring the @check component
        if (wrapper.hasClass('check')) {
            this.renderCheckError(wrapper, this.requiredMessage);

            return;
        }

        // the native email message is localised to the browser UI language, not the site language,
        // so it bypasses WPML entirely — use our own translated string instead
        let message: string = this.requiredMessage;
        const isWrongFormat: boolean = input.validity.patternMismatch || input.validity.tooShort || input.validity.tooLong;

        if (input.validity.typeMismatch && input.type === 'email') {
            message = this.emailMessage;
        } else if (isWrongFormat && this.formatMessages[input.name]) {
            message = this.formatMessages[input.name];
        } else if (!input.validity.valueMissing && input.validationMessage) {
            message = input.validationMessage;
        }

        let errorElement: JQuery = wrapper.children('.textfield__error');

        if (!errorElement.length) {
            errorElement = $('<div class="textfield__error" role="alert"></div>').appendTo(wrapper);
        }

        errorElement.text(message);
    }

    // mirrors the @check component's .check__error markup so styleguide and runtime stay in sync
    private renderCheckError(wrapper: JQuery, message: string): void {
        if (wrapper.children('.check__error').length) {
            return;
        }

        wrapper.append(
            `<div class="check__error" role="alert">${ Icon.render('error-outline', '', 'check__error-icon') }<span class="check__error-text">${ message }</span></div>`,
        );
    }

    private clearFieldError(input: HTMLInputElement): void {
        const wrapper: JQuery = $(input).closest('.textfield, .check');

        wrapper.removeClass(INVALID_CLASS);
        wrapper.children('.textfield__error, .check__error').remove();
    }

    private handleContinue: () => void = (): void => {
        if (!this.validatePanel('personal')) {
            return;
        }

        if (!this.formFilledTracked) {
            this.formFilledTracked = true;
            Helpers.pushDataLayerEvent('application_form_filled');
        }

        this.goToStep('consents');
    };

    private handleSubmit: (event: JQuery.TriggeredEvent) => void = (event: JQuery.TriggeredEvent): void => {
        event.preventDefault();

        if (!this.validatePanel('consents') || this.submitXhr && this.submitXhr.state() === 'pending') {
            return;
        }

        const formData: FormData = new FormData(this.element[0] as HTMLFormElement);

        // unchecked boxes are absent from FormData — set() writes a single explicit true/false per consent
        this.element.find('.calculator-steps__consents input[type="checkbox"]').each((index: number, element: HTMLElement): void => {
            const checkbox: HTMLInputElement = element as HTMLInputElement;

            formData.set(checkbox.name, checkbox.checked ? 'true' : 'false');
        });

        formData.append('action', 'calculator-steps-submit');

        this.element.addClass(LOADING_CLASS);

        this.submitXhr = Ajax.post(formData, { contentType: false });
        // Iute's decision: the applicant always sees the success screen, never a rejection or an error.
        const ALWAYS_SHOW_SUCCESS: boolean = true;

        this.submitXhr.done((response: ICalculatorStepsSubmitResponse): void => {
            if (response.success) {
                this.showResult('success', response.customerId || '');
            } else if (response.errorCode === 'pendingLoanApplication') {
                this.showResult(ALWAYS_SHOW_SUCCESS ? 'success' : 'pending');
            } else if (response.errorCode === 'probation') {
                this.showResult(ALWAYS_SHOW_SUCCESS ? 'success' : 'verification');
            } else {
                this.showErrors(response.errors || [], response.invalidFields || []);
            }
        });
        this.submitXhr.fail((jqXHR: JQueryXHR, textStatus: string): void => {
            if (textStatus !== 'abort') {
                this.showResult(ALWAYS_SHOW_SUCCESS ? 'success' : 'verification');
            }
        });
        this.submitXhr.always((): void => {
            this.element.removeClass(LOADING_CLASS);
        });
    };

    private trackApplicationSuccessful(customerId: string): void {
        /* eslint-disable @typescript-eslint/naming-convention */
        Helpers.pushDataLayerEvent('application_successful', {
            ecommerce: {
                // only an approved application has a customer id - otherwise generate a unique id
                transaction_id: customerId || CalculatorSteps.generateTransactionId(),
                value: parseInt(this.amountInput.val() as string, 10) || 0,
                currency: this.primaryCurrency,
                items: [{
                    item_id: (this.element.find('input[name="product_id"]').val() as string) || '',
                    item_name: window.location.href,
                }],
            },
        });
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    private static generateTransactionId(): string {
        return 'cs-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    }

    private showErrors(errors: string[], invalidFields: string[]): void {
        // show the errors on the earliest step that has an invalid field, so the user lands next to what needs fixing
        let panelIndex: number = STEPS.indexOf('consents');

        invalidFields.forEach((name: string): void => {
            const input: JQuery = this.element.find(`[name="${name}"]`);
            const wrapper: JQuery = input.closest('.textfield, .check');

            wrapper.addClass(INVALID_CLASS);

            // checkboxes get the same inline error-outline message as client-side validation
            if (wrapper.hasClass('check')) {
                this.renderCheckError(wrapper, this.requiredMessage);
            }

            const fieldIndex: number = STEPS.indexOf(input.closest('.calculator-steps__panel').data('panel') as string);

            if (fieldIndex > -1 && fieldIndex < panelIndex) {
                panelIndex = fieldIndex;
            }
        });

        const panel: string = STEPS[panelIndex];

        if (panel !== 'consents') {
            this.goToStep(panel);
        }

        const errorPanel: JQuery = this.element.find(`.calculator-steps__panel-error[data-error="${panel}"]`);

        errorPanel.find('.alert__content').text(errors.join(' '));
        errorPanel.addClass(VISIBLE_CLASS);
    }

    // Romanian number convention, kept in sync with formatMoney()/formatPercent() in CalculatorSteps.php
    private static formatNumber(value: number, isPercent: boolean = false): string {
        return isPercent ? Helpers.formatNumber(value, 2, ',', '') : Helpers.formatNumber(value, 2, ',', '.');
    }
}
